import { predicateSourceMigration } from "utils/filter";

import { LegendItemModel } from "models/LegendItem";
import { PropertyFilterModel } from "models/filter";
import { IGroupBy } from "models/groupBy";

export const checkedItemKey = (property: string, value: string): string =>
  JSON.stringify([property, value]);

/**
 * A value checked while collapsed into Others keeps its predicate but has no
 * visible row to rebuild its checked item from, so every write of the checked
 * set would drop it. Returns a checked item for each unmatched focus predicate.
 */
export function collapsedCheckedItems(
  groupBy: IGroupBy,
  propertiesFilter: PropertyFilterModel[],
  matchedPredicateIndices: Set<number>,
  remembered: Map<string, LegendItemModel>
): LegendItemModel[] {
  return propertiesFilter
    .flatMap((filter) => filter.predicates.map((predicate) => ({ filter, predicate })))
    .flatMap<LegendItemModel>(({ filter, predicate }, index) => {
      if (matchedPredicateIndices.has(index) || predicate.property !== groupBy.property) {
        return [];
      }
      const value = predicate.sourceKey || predicateSourceMigration(predicate);
      const item = remembered.get(checkedItemKey(groupBy.property, value));
      const isNotEqual = !!predicate.negatedOperator;
      if (!item) {
        // Nothing to rebuild it from (a restored saved filter, another tab), and
        // rebuilding a bare predicate could change it, so it is written back as-is.
        const retained = new LegendItemModel(value, groupBy.property);
        return [
          Object.assign(retained, {
            value,
            bin: groupBy.bin,
            isChecked: true,
            isNotEqual,
            isOthersRollup: !!predicate.rollup,
            retainedFilter: { ...filter, predicates: [predicate] }
          })
        ];
      }
      // Stamped like a visible row; the remembered item may also have been
      // unchecked or re-negated in place since.
      return [{ ...item, bin: groupBy.bin, isChecked: true, isNotEqual }];
    });
}
